import {
  type InfiniteData,
  type Query,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { ProviderManifest } from '@/types/core/provider-manifest';
import type { Page } from '@/types/shared/page';
import { isNilUUID, type UUID } from '@/utils/uuid';

import { providerQueryKeys } from './query-keys';

/**
 * List available providers from the backend catalog.
 */
export function useListAvailableProviders(
  tenantId?: UUID,
  opts?: {
    limit?: number;
    next?: string;
    /** Polling interval in ms. Pass a function to stop when data arrives. */
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<
            Page<ProviderManifest>,
            Error,
            InfiniteData<Page<ProviderManifest>, unknown>
          >
        ) => number | false | undefined);
  }
): UseInfiniteQueryResult<
  InfiniteData<Page<ProviderManifest>, unknown>,
  Error
> {
  return useInfiniteQuery<
    Page<ProviderManifest>,
    Error,
    InfiniteData<Page<ProviderManifest>, unknown>
  >({
    queryKey: providerQueryKeys.availableProviderList(tenantId, opts?.limit),
    enabled: Boolean(tenantId) && !isNilUUID(tenantId),
    queryFn: (context: QueryFunctionContext) => {
      if (!tenantId) {
        throw new Error('Missing tenant id');
      }
      const pageParam = context.pageParam as string | undefined;
      return providerService.listAvailableProviders(tenantId, {
        limit: opts?.limit,
        next: pageParam,
      });
    },
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
    ...(opts?.refetchInterval !== undefined && {
      refetchInterval: opts.refetchInterval,
    }),
  });
}
